import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaEllipsisV } from "react-icons/fa";

export interface ActionMenuItem {
    label: string;
    onClick: () => void;
    danger?: boolean;
}

interface ActionMenuProps {
    items: ActionMenuItem[];
    /** Width of the menu in px. Defaults to 160. */
    menuWidth?: number;
}

/**
 * Tiny dropdown that renders the menu via createPortal into document.body, so
 * it ignores any overflow:hidden on parent containers (e.g. react-data-table
 * rows/cells). Closes on outside click and on Escape.
 */
const ActionMenu: React.FC<ActionMenuProps> = ({ items, menuWidth = 160 }) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const updatePosition = useCallback(() => {
        const btn = triggerRef.current;
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        // Anchor below the button, right-aligned so the menu hugs the right edge.
        const top = r.bottom + 4;
        const left = Math.max(8, r.right - menuWidth);
        setPos({ top, left });
    }, [menuWidth]);

    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;
        const onScrollOrResize = () => updatePosition();
        window.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize);
        return () => {
            window.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                menuRef.current?.contains(target) ||
                triggerRef.current?.contains(target)
            ) {
                return;
            }
            setOpen(false);
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="btn btn-link p-0 text-secondary"
                data-cy="action-menu-trigger"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <FaEllipsisV />
            </button>

            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        role="menu"
                        className="bg-white border rounded shadow"
                        style={{
                            position: "fixed",
                            top: pos.top,
                            left: pos.left,
                            width: menuWidth,
                            zIndex: 3000,
                            padding: "0.25rem 0",
                        }}
                    >
                        {items.map((item, idx) => (
                            <button
                                key={idx}
                                type="button"
                                className={`btn btn-link w-100 text-start ${
                                    item.danger ? "text-danger" : ""
                                }`}
                                data-cy={`action-menu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                style={{
                                    padding: "0.4rem 0.85rem",
                                    textDecoration: "none",
                                }}
                                onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#f6f6fa")
                                }
                                onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor = "transparent")
                                }
                                onClick={() => {
                                    setOpen(false);
                                    item.onClick();
                                }}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
        </>
    );
};

export default ActionMenu;
